import { computed, inject } from '@angular/core';
import { HttpStatusCode } from '@angular/common/http';
import { patchState, signalStore, withMethods } from '@ngrx/signals';
import {
  entityConfig,
  upsertEntities,
  upsertEntity,
  withEntities,
} from '@ngrx/signals/entities';
import { firstValueFrom } from 'rxjs';
import { InteractionService } from '@thiememeulenhoff/ng-api-gateway';
import {
  Attempt,
  InteractionStatus,
  ProgressItem,
  Result,
  SelfScoreType,
  statusGreaterThan,
} from '@thiememeulenhoff/types';
import { withRequestStatus } from '../_utils/request-status.feature';
import { ResultEntry } from './interaction.types';

const progressConfig = entityConfig({
  entity: {} as ProgressItem,
  collection: 'progress',
  selectId: (e: ProgressItem) => e.contentIdentifier,
});

const resultsConfig = entityConfig({
  entity: {} as ResultEntry,
  collection: 'results',
  selectId: (e: ResultEntry) => e.contentIdentifier,
});

export function flattenProgress(
  progress: ProgressItem,
  parent: ProgressItem | undefined = undefined,
): Array<ProgressItem> {
  const entities: Array<ProgressItem> = [];
  const children = progress.children || [];
  const copy = { ...progress };
  delete copy.children;

  entities.push({
    ...copy,
    parentIdentifier: parent?.contentIdentifier ?? '',
    done: statusGreaterThan(
      progress.status as InteractionStatus,
      InteractionStatus.Completed,
    ),
  });

  children.forEach((child) => {
    entities.push(...flattenProgress(child, copy));
  });

  return entities;
}

export const InteractionStore = signalStore(
  { providedIn: 'root' },
  withEntities(progressConfig),
  withEntities(resultsConfig),
  withRequestStatus(),
  withMethods((store, interactionService = inject(InteractionService)) => {
    function updateProgress(
      contentIdentifier: string,
      item: Partial<ProgressItem>,
    ): void {
      const current = store.progressEntityMap()[contentIdentifier];

      if (current) {
        patchState(
          store,
          upsertEntity({ ...current, ...item }, progressConfig),
        );
      }
    }

    function updateProgressFromResult(result: Result): void {
      updateProgress(result.contentIdentifier, {
        score: result?.result?.score,
        status: result.status,
        done: statusGreaterThan(result.status, InteractionStatus.Completed),
      });
      patchState(
        store,
        upsertEntity({ ...result } as ResultEntry, resultsConfig),
      );
    }

    function upsertProgress(item: ProgressItem): void {
      const entities = flattenProgress(item);
      patchState(store, upsertEntities(entities, progressConfig));

      entities.forEach((entity) => {
        const currentResult =
          store.resultsEntityMap()[entity.contentIdentifier];

        if (currentResult && entity.status) {
          patchState(
            store,
            upsertEntity(
              { ...currentResult, status: entity.status as InteractionStatus },
              resultsConfig,
            ),
          );
        }
      });
    }

    function updateDraft(
      draft: Attempt,
      contentIdentifier: string,
      isSelfTest: boolean,
    ): void {
      // Note that we patch state with a client side model, draft endpoint does not return a result
      const currentResult = store.resultsEntityMap()[contentIdentifier];

      if (currentResult) {
        const newResult = { ...currentResult };
        newResult.draft = draft;
        newResult.status =
          // This logic is duplicate of our server side workflow
          isSelfTest && currentResult.numberOfAttempts > 0
            ? InteractionStatus.DraftRetry
            : InteractionStatus.Draft;

        updateProgressFromResult(newResult);
      }
    }

    return {
      progressById(id: string) {
        return computed<ProgressItem | undefined>(
          () => store.progressEntityMap()[id],
        );
      },

      progressByParentId(contentIdentifier: string) {
        return computed(() =>
          store
            .progressEntities()
            .filter((e) => e.parentIdentifier === contentIdentifier),
        );
      },

      progressByParentIds(contentIdentifiers: Array<string>) {
        return computed(() =>
          store
            .progressEntities()
            .filter((e) =>
              contentIdentifiers.includes(e.parentIdentifier ?? ''),
            ),
        );
      },

      progressWithChildrenById(id: string) {
        return computed((): ProgressItem | undefined => {
          const parent = store.progressEntityMap()[id];

          if (!parent) {
            return parent;
          }

          const children = store
            .progressEntities()
            .filter((e) => e.parentIdentifier === id);

          return { ...parent, children } as ProgressItem;
        });
      },

      resultById(contentIdentifier: string) {
        return computed(() => store.resultsEntityMap()[contentIdentifier]);
      },

      resultAttemptsById(contentIdentifier: string) {
        return computed(() => store.resultsEntityMap()[contentIdentifier]);
      },

      async fetchProgress(
        arrangementCode: string,
        contentIdentifier: string,
        depth = 1,
      ): Promise<ProgressItem> {
        const progress = await firstValueFrom(
          interactionService.getProgressById(
            arrangementCode,
            contentIdentifier,
            depth,
          ),
        );
        upsertProgress(progress);

        return progress;
      },

      async fetchResult(
        arrangementCode: string,
        contentIdentifier: string,
      ): Promise<Result> {
        const result = await firstValueFrom(
          interactionService.getResultById(arrangementCode, contentIdentifier),
        );
        updateProgressFromResult(result);

        return result;
      },

      async fetchAttemptsResult(
        arrangementCode: string,
        contentIdentifier: string,
        userIdentifier?: string,
      ): Promise<Result> {
        const result = await firstValueFrom(
          interactionService.getResultAttemptsById(
            arrangementCode,
            contentIdentifier,
            userIdentifier,
          ),
        );
        updateProgressFromResult(result);

        return result;
      },

      fetchAnswer(arrangementCode: string, contentIdentifier: string) {
        return firstValueFrom(
          interactionService.getAnswerById(arrangementCode, contentIdentifier),
        );
      },

      submitAttempt(attempt: Attempt) {
        return firstValueFrom(interactionService.submitAttempt(attempt));
      },

      async saveDraft(
        draft: Attempt,
        contentIdentifier: string,
        isSelfTest: boolean,
      ): Promise<HttpStatusCode | Result> {
        const status = await firstValueFrom(
          interactionService.saveDraft(draft),
        );

        if (
          status === HttpStatusCode.Created ||
          status === HttpStatusCode.NoContent
        ) {
          updateDraft(draft, contentIdentifier, isSelfTest);
        }

        if (isSelfTest && status === HttpStatusCode.ResetContent) {
          return this.fetchResult(draft.arrangementCode, contentIdentifier);
        }

        return status;
      },

      retry(contentIdentifier: string): void {
        const currentResult = store.resultsEntityMap()[contentIdentifier];

        if (currentResult) {
          const result = { ...currentResult, status: InteractionStatus.Draft };
          patchState(store, upsertEntity(result, resultsConfig));
        }
      },

      async startSelfTest(
        arrangementCode: string,
        contentPath: string,
      ): Promise<ProgressItem> {
        const contentPathArray = contentPath.split('/');
        await firstValueFrom(
          interactionService.startSelfTest(arrangementCode, contentPath),
        );

        return this.fetchProgress(
          arrangementCode,
          contentPathArray[contentPathArray.length - 1],
        );
      },

      submitSelfTest(arrangementCode: string, contentPath: string) {
        return firstValueFrom(
          interactionService.submitSelfTest(arrangementCode, contentPath),
        );
      },

      completeSelfTest(arrangementCode: string, contentPath: string) {
        return firstValueFrom(
          interactionService.completeSelfTest(arrangementCode, contentPath),
        );
      },

      deleteSelfTest(arrangementCode: string, contentPath: string) {
        return firstValueFrom(
          interactionService.deleteSelfTest(arrangementCode, contentPath),
        );
      },

      submitSelfScore(selfScore: SelfScoreType) {
        return firstValueFrom(interactionService.submitSelfScore(selfScore));
      },

      closeAttempt(arrangementCode: string, contentIdentifier: string) {
        return firstValueFrom(
          interactionService.closeAttempt(arrangementCode, contentIdentifier),
        );
      },

      clearResult(arrangementCode: string, contentIdentifier: string) {
        return firstValueFrom(
          interactionService.clearResultById(
            arrangementCode,
            contentIdentifier,
          ),
        );
      },
    };
  }),
);
